<template>
    <v-card tile :loading="loading">
        <!-- Loading Overlay -->
        <v-overlay :value="loading || exportingPdf" fixed z-index="999">
            <div class="text-center">
                <v-progress-circular
                    indeterminate
                    size="70"
                    width="7"
                    color="secondary"
                ></v-progress-circular>
                <div class="mt-4 white--text" style="font-size: 18px;">
                    {{ exportingPdf ? ($t('ExportingPDF') || 'Exporting PDF...') : ($t('LoadingAnalytics') || 'Loading Analytics...') }}
                </div>
            </div>
        </v-overlay>

        <div class="card-title">{{ $t('Analytics') }}</div>
        <v-card-title>
            <v-spacer />
            <router-link :to="{ name: 'survey-list'}" custom>
                <v-btn fab small class="white--text" color="secondary" role="link">
                    <v-icon>mdi-arrow-left-circle</v-icon>
                </v-btn>
            </router-link>
        </v-card-title>
        <v-card-text>
            <v-row>
                <v-col cols="4">
                    <v-autocomplete searchable multiple chips clearable dense outlined v-model.lazy="selectedQuestions"
                                    :items="questionsSurvey"
                                    item-value="id"
                                    item-text="questionName"
                                    :menu-props="{ maxHeight: '150' }"
                                    style="z-index: 999999"
                                    :label="$t('Select')">

                        <template v-slot:prepend-item>
                            <v-list-item @click="selectAllItems">
                                <v-list-item-content>{{ $t('SelectAll') }}</v-list-item-content>
                            </v-list-item>
                        </template>
                        <template v-slot:item="{ item }">
                            <v-list-item-content>
                                <template v-if="item !== 'selectAll'">
                                    <h4>{{ item.questionName }}</h4>
                                </template>
                                <template v-else>
                                    <v-list-item-content>{{ $t('SelectAll') }}</v-list-item-content>
                                </template>
                            </v-list-item-content>
                        </template>
                        <template v-slot:selection="{ item }">
                            <v-chip :key="item.id"
                                    close
                                    @click:close="removeItem(item.id)">
                                {{ item.questionName }}
                            </v-chip>
                        </template>

                    </v-autocomplete>
                </v-col>
                <v-col cols="4">
                    <v-autocomplete searchable multiple chips clearable dense outlined v-model.lazy="selectedGISItems"
                                    :items="gisOptions"
                                    item-value="label"
                                    item-text="label"
                                    :menu-props="{ maxHeight: '200' }"
                                    style="z-index: 999999"
                                    :label="$t('FilterByLocation')">

                        <template v-slot:prepend-item>
                            <v-list-item @click="selectAllGISItems">
                                <v-list-item-content>{{ $t('SelectAll') }}</v-list-item-content>
                            </v-list-item>
                        </template>
                        <template v-slot:item="{ item }">
                            <v-list-item-content>
                                <h4>{{ item.label }} <small class="grey--text">({{ item.type }})</small></h4>
                            </v-list-item-content>
                        </template>
                        <template v-slot:selection="{ item }">
                            <v-chip :key="item.label"
                                    close small
                                    @click:close="removeGISItem(item.label)">
                                {{ item.label }}
                            </v-chip>
                        </template>

                    </v-autocomplete>
                </v-col>
                <v-col cols="2">
                    <v-select dense outlined v-model="selectedFormat" :items="exportOptions" :label="$t('ExportFormat')"></v-select>
                </v-col>

            </v-row>
            <v-row>
                <!-- From Date -->
                <v-col cols="12" sm="4">
                    <v-menu v-model="fromMenu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="290px">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="fromDate" dense outlined
                                          :label="$t('FromDate')"
                                          prepend-icon="mdi-calendar"
                                          readonly
                                          v-bind="attrs"
                                          v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="fromDate"
                                       :max="toDate">
                            <v-spacer></v-spacer>
                            <v-btn text @click="fromMenu = false">Cancel</v-btn>
                            <v-btn text @click="applyFromDate">OK</v-btn>
                        </v-date-picker>
                    </v-menu>
                </v-col>

                <!-- To Date -->
                <v-col cols="12" sm="4">
                    <v-menu v-model="toMenu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="290px">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="toDate" dense outlined
                                          :label="$t('ToDate')"
                                          prepend-icon="mdi-calendar"
                                          readonly
                                          v-bind="attrs"
                                          v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="toDate"
                                       :min="fromDate">
                            <v-spacer></v-spacer>
                            <v-btn text @click="toMenu = false">Cancel</v-btn>
                            <v-btn text @click="applyToDate">OK</v-btn>
                        </v-date-picker>
                    </v-menu>
                </v-col>
                <v-col cols="12" sm="4">
                    <v-btn class="white--text v-btn v-btn--has-bg theme--light secondary" @click="exportData">{{ $t('Download') }}</v-btn>
                </v-col>
            </v-row>

            <v-row>
                <h2 style="padding:10px;">
                    <span class="secondary-color">{{$t('SurveyTitle')}}</span>{{ surveyName }}
                </h2>
                <h2 style="padding:10px;">
                    <span class="secondary-color">{{$t('AvgTimeToFinish')}}</span>{{ averageDifference }}
                </h2>
            </v-row>

            <v-row class="mt-4">
                <v-tabs grow v-model="tab" background-color="secondary" slider-color="primary" class="pa-4">
                    <v-tab style="font-weight:bold">{{ $t('PieChart') }}</v-tab>
                    <v-tab style="font-weight:bold">{{ $t('BarChart') }}</v-tab>
                    <v-tab style="font-weight:bold">{{ $t('LineChart') }}</v-tab>
                    <v-tab style="font-weight:bold">{{ $t('Table') }}</v-tab>

                    <v-tabs-items v-model="tab" class="py-5 px-1">
                        <v-tab-item>
                            <v-row>
                                <v-col cols="12">
                                    <v-row>
                                        <template v-for="(question, index) in resultsFilter">
                                            <v-col :cols="isMobile ? 12 : 4" :key="index"
                                                   v-if="question.questionOptions.length > 0 && (question.questionTypeId != shortAnswer && question.questionTypeId != textEditor &&
                    question.questionTypeId != textArea && question.questionTypeId != image && question.questionTypeId != vedioLink
                    && question.questionTypeId != maps)">
                                                <Doghnut ref="donutApex" v-if="question.questionOptions[0]" :key="index" :series="question.questionOptions[0].series" :labels="question.questionOptions[0].labels" :title="question.questionName" />
                                            </v-col>
                                        </template>
                                    </v-row>
                                </v-col>
                            </v-row>
                            <v-row v-if="filteredGISData && filteredGISData.length > 0 && filteredGISData[0].labels && filteredGISData[0].labels.length>0">
                                <v-col cols="12">
                                    <h2 class="theme--light white--text secondary" style="padding:5px;margin:5px">{{ $t('ParticipantLocationData') }}</h2>
                                    <v-row>

                                        <template v-for="(gIS, index) in filteredGISData">
                                            <v-col :cols="isMobile ? 12 : 4" :key="index">
                                                <Doghnut ref="donutApexGis" :key="index" :series="gIS.series" :labels="gIS.labels" :title="gIS.gisName" />
                                            </v-col>
                                        </template>
                                    </v-row>
                                </v-col>
                            </v-row>
                        </v-tab-item>
                        <v-tab-item>
                            <v-row>
                                <v-col cols="12">
                                    <v-row>
                                        <template v-for="(question, index) in resultsFilter">
                                            <v-col :cols="isMobile ? 12 : 4" :key="index" v-if="question.questionOptions.length > 0  && (question.questionTypeId != shortAnswer && question.questionTypeId != textEditor &&
                   question.questionTypeId != textArea && question.questionTypeId != image && question.questionTypeId != vedioLink
                   && question.questionTypeId != maps)">
                                                <bar ref="barApex" :key="index" v-if="question.questionOptions[0]" :series="getSeriesBar(question.questionOptions[0])" :categories="question.questionOptions[0].labels" :title="question.questionName" />
                                            </v-col>
                                        </template>
                                    </v-row>
                                </v-col>
                            </v-row>
                            <v-row v-if="filteredGISData && filteredGISData.length > 0 && filteredGISData[0].labels && filteredGISData[0].labels.length>0">
                                <v-col cols="12">
                                    <h2 class="theme--light white--text secondary" style="padding:5px;margin:5px">{{ $t('ParticipantLocationData') }}</h2>
                                    <v-row>

                                        <template v-for="(gIS, index) in filteredGISData">
                                            <v-col :cols="isMobile ? 12 : 4" :key="index">
                                                <bar ref="barApexGis" :key="index" :series="getSeriesBar(gIS)" :categories="gIS.labels" :title="gIS.gisName" />

                                            </v-col>
                                        </template>
                                    </v-row>
                                </v-col>
                            </v-row>
                        </v-tab-item>
                        <v-tab-item>
                            <v-row>
                                <v-col cols="12">
                                    <v-row>
                                        <template v-for="(question, index) in resultsFilter">
                                            <v-col :cols="isMobile ? 12 : 4" :key="index"
                                                   v-if="question.questionOptions.length > 0 && (question.questionTypeId != shortAnswer && question.questionTypeId != textEditor &&
                   question.questionTypeId != textArea && question.questionTypeId != image && question.questionTypeId != vedioLink
                   && question.questionTypeId != maps)">
                                                <LineChart ref="lineApex" :key="index" v-if="question.questionOptions[0]" :series="getSeriesBar(question.questionOptions[0])" :categories="question.questionOptions[0].labels" :title="question.questionName" />

                                            </v-col>
                                        </template>
                                    </v-row>
                                </v-col>
                            </v-row>
                            <v-row v-if="filteredGISData && filteredGISData.length > 0 && filteredGISData[0].labels && filteredGISData[0].labels.length>0">
                                <v-col cols="12">
                                    <h2 class="theme--light white--text secondary" style="padding:5px;margin:5px">{{ $t('ParticipantLocationData') }}</h2>
                                    <v-row>

                                        <template v-for="(gIS, index) in filteredGISData">
                                            <v-col :cols="isMobile ? 12 : 4" :key="index">
                                                <LineChart ref="lineApexGis" :key="index" :series="getSeriesBar(gIS)" :categories="gIS.labels" :title="gIS.gisName" />
                                            </v-col>
                                        </template>
                                    </v-row>
                                </v-col>
                            </v-row>
                        </v-tab-item>
                        <v-tab-item>
                            <v-row>
                                <v-col ref="tableChart" cols="12">
                                    <v-row>
                                        <template v-for="(question, index) in resultsFilter">
                                            <v-col :cols="isMobile ? 12 : 4" :key="index" v-if="question.questionTypeId != image && question.questionTypeId != vedioLink && question.questionTypeId != maps">
                                                <h4>{{question.questionName}}</h4>
                                                <div v-if="question.questionOptions.length > 0">
                                                    <template v-for="(questionopt, indexopt) in question.questionOptions">
                                                        <v-col :key="indexopt" v-if="questionopt.labels.length > 0" style="height:200px; overflow-y:scroll">
                                                            <template v-for="(questionlbl, indexlbl) in questionopt.labels">
                                                                <div :key="indexlbl">
                                                                    <span :style="tructate(questionopt.questionTypeId)">{{ questionlbl }}</span>
                                                                    <v-chip label small class="float-end" v-if="questionopt.questionTypeId != shortAnswer && questionopt.questionTypeId != textArea && questionopt.questionTypeId != textEditor">{{ questionopt.series[indexlbl] }}</v-chip>
                                                                </div>
                                                                <v-divider :key="indexlbl+'_sep'" v-if="indexlbl !== questionopt.labels - 1" class="my-2"></v-divider>
                                                            </template>
                                                        </v-col>
                                                    </template>
                                                </div>
                                            </v-col>
                                        </template>
                                    </v-row>
                                </v-col>
                            </v-row>
                            <v-row v-if="filteredGISData && filteredGISData.length > 0 && filteredGISData[0].labels && filteredGISData[0].labels.length>0">
                                <v-col ref="tableChartGis" cols="12">
                                    <h2 class="theme--light white--text secondary" style="padding:5px;margin:5px">{{ $t('ParticipantLocationData') }}</h2>
                                    <v-row>
                                        <template v-for="(gis, index) in filteredGISData">
                                            <v-col :cols="isMobile ? 12 : 4" :key="index">
                                                <p style="font-family:Tajawal, sans-serif !important;font-weight: bold; color: black; font-size: 14px; text-align: right; direction: rtl; ">{{gis.gisName}}</p>
                                                <v-col>
                                                    <template>
                                                        <v-col>
                                                            <template v-for="(questionlbl, indexlbl) in gis.labels">
                                                                <div ref="divTableP" :key="indexlbl+'_div'" style="font-family: Tajawal, sans-serif !important; display: flex; justify-content: space-between; text-align: right; direction: rtl; margin: 19px 3px 0px 20px; ">
                                                                    <div style="direction:rtl;float:right;"><p>{{ questionlbl }}</p></div>
                                                                    <div style="direction: rtl; float: left;"><p style="font-size: 14px; text-align: right; padding: 0px; margin: 0px;">{{ gis.series[indexlbl] }}</p></div>
                                                                </div>
                                                                <v-divider :key="indexlbl+'_sep'" v-if="indexlbl !== gis.labels - 1" class="my-2"></v-divider>
                                                            </template>
                                                        </v-col>
                                                    </template>
                                                </v-col>
                                            </v-col>
                                        </template>
                                    </v-row>
                                </v-col>
                            </v-row>
                        </v-tab-item>
                    </v-tabs-items>
                </v-tabs>
            </v-row>

        </v-card-text>
    </v-card>
</template>

<script>
    import SurveyService from '@/services/SurveyService.js'
    import Doghnut from '@/components/app/charts/Doghnut.vue'
    import Bar from '@/components/app/charts/Bar.vue'
    import LineChart from '@/components/app/charts/Line.vue'
    import { FormMethodEnum } from '@/helpers/constants/enumerations.js'
    import ExcelJS from 'exceljs'
    import { saveAs } from 'file-saver'
    import { Chart, registerables } from 'chart.js'
    Chart.register(...registerables)
    import jsPDF from 'jspdf'
    import { useStripHtml } from '@/helpers/common.js'



    export default {
        components: {
            Doghnut, Bar, LineChart
        },

        data() {
            return {
                absolute: true,
                tab: null,
                surveyName: '',
                loading: false,
                exportingPdf: false,
                results: [],
                resultsFilter: [],
                resultsGISFilter: [],
                filteredGISData: [],
                selectedQuestions: [],
                selectedGISItems: [],
                gisOptions: [],
                questionsSurvey: [],
                averageDifference: '',
                selectedFormat: 'xlsx',
                textEditor: FormMethodEnum.TextEditor,
                shortAnswer: FormMethodEnum.ShortAnswer,
                textArea: FormMethodEnum.TextArea,
                image: FormMethodEnum.Image,
                vedioLink: FormMethodEnum.VedioLink,
                maps: FormMethodEnum.Maps,
                nps: FormMethodEnum.NPS,
                exportOptions: [
                    { value: 'pdf', text: 'PDF' },
                    { value: 'csv', text: 'CSV' },
                    { value: 'xlsx', text: 'XLSX' }
                ],
                isMobile: false,
                fromDate: null,
                toDate: null,
                fromMenu: false,
                toMenu: false,
            }
        },


        created() {
            if (this.$route.params.id) {
                this.loadSurveyAnalytics(this.$route.params.id)
            }
        },

        mounted() {
            this.isMobile = window.innerWidth <= 600
            window.addEventListener('resize', this.updateIsMobile)
        },



        beforeDestroy() {
            window.removeEventListener('resize', this.updateIsMobile)
        },

        watch: {
            selectedQuestions() {
                if (this.selectedQuestions.length === 0) {
                    this.resultsFilter = this.results
                } else {
                    this.resultsFilter = this.results.filter(item => this.selectedQuestions.includes(item.questionId))
                }
            },
            selectedGISItems(newVal, oldVal) {
                // Trigger backend reload when GIS filter changes
                if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
                    this.loadSurveyAnalytics(this.$route.params.id)
                }
            }
        },
        methods: {
            applyFromDate() {
                this.fromMenu = false
                this.loadSurveyAnalytics(this.$route.params.id)
            },
            applyToDate() {
                this.toMenu = false
                this.loadSurveyAnalytics(this.$route.params.id)
            },
            tructate(questionTypeId) {
                switch (questionTypeId) {
                    case this.shortAnswer:
                    case this.textArea:
                    case this.textEditor:
                        return 'font-size:14px; max-height:150px; overflow-y:auto'
                    default:
                        return 'font-size: 14px;text-align: right;padding: 0px;margin: 0px;'
                }
            },

            updateIsMobile() {
                this.isMobile = window.innerWidth <= 600
            },
            loadSurveyAnalytics(surveyId) {
                this.loading = true
                SurveyService.loadSurveyAnalytics(surveyId, this.fromDate, this.toDate, this.selectedGISItems).then((res) => {
                    this.results = (res.data.questionAnalyticsListItemDto || []).map(item => ({
                        ...item,
                        questionName: item.questionName ? useStripHtml(item.questionName) : item.questionName
                    }))
                    this.resultsFilter = this.results
                    
                    // Filter out null labels from GIS data
                    this.resultsGISFilter = (res.data.partiGISLD || []).map(gis => {
                        if (gis.labels && gis.series) {
                            const validIndices = []
                            gis.labels.forEach((label, index) => {
                                if (label != null && label !== '' && label !== undefined) {
                                    validIndices.push(index)
                                }
                            })
                            return {
                                ...gis,
                                labels: validIndices.map(i => gis.labels[i]),
                                series: validIndices.map(i => gis.series[i])
                            }
                        }
                        return gis
                    }).filter(gis => gis.labels && gis.labels.length > 0)
                    
                    // Set filtered data for display
                    this.filteredGISData = this.resultsGISFilter
                    
                    // Extract location values for dropdown (only on initial load, not when filtering)
                    if (this.gisOptions.length === 0) {
                        const locationOptions = []
                        this.resultsGISFilter.forEach(gis => {
                            if (gis.labels) {
                                gis.labels.forEach(label => {
                                    if (label && !locationOptions.some(o => o.label === label)) {
                                        locationOptions.push({
                                            label: label,
                                            type: gis.gisName
                                        })
                                    }
                                })
                            }
                        })
                        this.gisOptions = locationOptions
                    }
                    
                    this.surveyName = useStripHtml(res.data.surveyName)
                    this.averageDifference = res.data.averageDifference
                    
                    // Only populate questions on initial load
                    if (this.questionsSurvey.length === 0) {
                        this.results.forEach(item => {
                            this.questionsSurvey.push({
                                id: item.questionId,
                                questionName: item.questionName ? useStripHtml(item.questionName) : item.questionName
                            })
                        })
                    }
                    if (this.gisOptions.length > 0) {
                        this.questionsSurvey.push({ id: this.gisOptions[0].label, questionName: this.$t('ParticipantLocationData') }) 
                    }
                    
                    

                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },

            getSeriesBar(QuestionOption) {
                const labels = QuestionOption.labels
                const series = QuestionOption.series

                const seriesBar = []
                const data = []
                for (let i = 0; i < series.length; i++) {
                    data.push(series[i])
                }
                seriesBar.push({ data: data })
                return seriesBar
            }


            ,
            removeItem(id) {
                const index = this.selectedQuestions.indexOf(id)
                if (index !== -1) {
                    this.selectedQuestions.splice(index, 1)
                }
            },
            selectAllItems() {
                this.selectedQuestions = []

                this.questionsSurvey.forEach(item => {
                    this.selectedQuestions.push(item.id)
                })
            },
            removeGISItem(label) {
                const idx = this.selectedGISItems.indexOf(label)
                if (idx !== -1) {
                    this.selectedGISItems.splice(idx, 1)
                }
            },
            selectAllGISItems() {
                this.selectedGISItems = this.gisOptions.map(item => item.label)
            },
            exportData() {
                if (this.selectedFormat === 'pdf') {
                    this.exportToPDF()
                } else if (this.selectedFormat === 'csv') {
                    this.exportToCSV()
                } else if (this.selectedFormat === 'xlsx') {
                    this.exportToXlsx()
                }
                this.clearSelection()
            },
            clearSelection() {
                this.selectedFormat = ''
            },
            exportToPDF() {
                switch (this.tab) {
                    case 0:
                        this.exportToPDFDonutAsync()
                        break
                    case 1:
                        this.exportToPDFBarAsync()
                        break
                    case 2:
                        this.exportPdfLineAsync()
                        break
                    case 3:
                        this.exportToPDFTableAsync()
                        break
                }

            },
            exportToCSV() {
                this.loading = true
                const csvContent = this.resultsFilter.map(question => {
                    if (question.questionOptions.length > 0) {
                        const labels = question.questionOptions[0].labels
                        const series = question.questionOptions[0].series
                        if (labels && series && labels.length === series.length) {

                            const total = series.reduce((acc, val) => acc + val, 0)

                            const dataRows = labels.map((label, index) => {
                                const percentage = ((series[index] / total) * 100).toFixed(0) + '%'
                                return [label, series[index], percentage]
                            })

                            const chartFormattedData = [[[question.questionName], 'العدد', 'النسبة المئوية'], ...dataRows]
                            const chartData = chartFormattedData.map(row => row.join(',')).join('\n')
                            return chartData
                        }
                    }
                    return null
                }).filter(data => data !== null).join('\n\n')

                var csvGisContent = ''
                if (this.resultsGISFilter.length > 0) {
                    csvGisContent = this.resultsGISFilter.map(gis => {

                        const labels = gis.labels
                        const series = gis.series
                        if (labels && series && labels.length === series.length) {

                            const total = series.reduce((acc, val) => acc + val, 0)

                            const dataRows = labels.map((label, index) => {
                                const percentage = ((series[index] / total) * 100).toFixed(0) + '%'
                                return [label, series[index], percentage]
                            })

                            const chartFormattedData = [[[gis.gisName], 'العدد', 'النسبة المئوية'], ...dataRows]
                            const chartData = chartFormattedData.map(row => row.join(',')).join('\n')
                            return chartData
                        }

                    }).filter(data => data !== null).join('\n\n')
                }
                const utf8BOM = '\uFEFF'
                const title = '***** بيانات موقع المشارك *****'
                const csvContentWithBOM = utf8BOM + csvContent + '\n\n' + title + '\n\n' + utf8BOM + csvGisContent

                const blob = new Blob([csvContentWithBOM], { type: 'text/csv;charset=utf-8' })
                saveAs(blob, 'charts_data.csv')
                this.loading = false
            },


            async exportToXlsx() {
                this.loading = true

                const utf8BOM = '\uFEFF'
                const title = '***** بيانات موقع المشارك *****'

                // Function to create data for chart
                const createChartData = (labels, series) => {
                    const total = series.reduce((acc, val) => acc + val, 0)
                    const dataRows = labels.map((label, index) => {
                        const percentage = ((series[index] / total) * 100).toFixed(0) + '%'
                        return [label, series[index], percentage]
                    })
                    return [['Label', 'Value', 'Percentage'], ...dataRows]
                }

                // Process survey data
                const processSurveyData = (resultsFilter) => {
                    return resultsFilter.map(question => {
                        if (question.questionOptions && question.questionOptions.length > 0) {
                            const labels = question.questionOptions[0].labels
                            const series = question.questionOptions[0].series
                            if (labels && series && labels.length === series.length) {
                                return {
                                    questionName: question.questionName,
                                    data: createChartData(labels, series),
                                    labels: labels,
                                    series: series
                                }
                            }
                        }
                        return null
                    }).filter(data => data !== null)
                }

                // Process GIS data
                const processGisData = (resultsGISFilter) => {
                    return resultsGISFilter.map(gis => {
                        if (gis.labels && gis.series) {
                            const labels = gis.labels
                            const series = gis.series
                            if (labels.length === series.length) {
                                return {
                                    gisName: gis.gisName,
                                    data: createChartData(labels, series),
                                    labels: labels,
                                    series: series
                                }
                            }
                        }
                        return null
                    }).filter(data => data !== null)
                }

                const surveyData = processSurveyData(this.resultsFilter)
                const gisData = processGisData(this.resultsGISFilter)

                const workbook = new ExcelJS.Workbook()
                const worksheet = workbook.addWorksheet('Charts Data')

                // Adding survey data to worksheet
                surveyData.forEach((survey) => {
                    worksheet.addRow([survey.questionName])
                    survey.data.forEach(row => worksheet.addRow(row))
                    worksheet.addRow([])
                })

                // Adding GIS data to worksheet
                worksheet.addRow([title])
                gisData.forEach((gis) => {
                    worksheet.addRow([gis.gisName])
                    gis.data.forEach(row => worksheet.addRow(row))
                    worksheet.addRow([])
                })

                // Function to add a chart image to the worksheet
                const addChartImage = async (ws, startRow, chart, colnum) => {
                    const imageData = await chart.capture()

                    const imageId = workbook.addImage({
                        base64: imageData,
                        extension: 'jpeg'
                    })
                    ws.addImage(imageId, {
                        tl: { col: colnum, row: startRow },
                        ext: { width: 400, height: 300 }
                    })
                }

                // Adding chart images for survey data
                let rowIndex = worksheet.lastRow.number + 2
                const charts = this.$refs.donutApex
                const chartsGis = this.$refs.donutApexGis
                if (charts) {
                    var col = 7
                    for (let i = 0; i < charts.length; i++) {
                        const chart = charts[i]
                        await addChartImage(worksheet, rowIndex, chart, col * i)

                    }
                    rowIndex += 20
                }
                if (chartsGis) {
                    var col = 7
                    for (let i = 0; i < chartsGis.length; i++) {
                        const chart = chartsGis[i]
                        await addChartImage(worksheet, rowIndex, chart, col * i)

                    }
                    rowIndex += 20
                }


                // Write workbook to buffer and save as Excel file
                workbook.xlsx.writeBuffer().then(buffer => {
                    const xlsxBlob = new Blob([buffer], { type: 'application/octet-stream' })
                    saveAs(xlsxBlob, 'charts_data.xlsx')
                }).finally(() => {
                    this.loading = false
                })
            },

            async exportToPDFDonutAsync() {
                this.exportingPdf = true
                // Use $nextTick to ensure loading indicator is visible
                await this.$nextTick()
                await new Promise(resolve => setTimeout(resolve, 100))
                
                try {
                    const charts = this.$refs.donutApex || []
                    const chartsGis = this.$refs.donutApexGis || []
                    const allCharts = [...charts, ...chartsGis]
                    const imgs = []
                    
                    for (let i = 0; i < allCharts.length; i++) {
                        try {
                            const imgData = await allCharts[i].capture()
                            if (imgData) imgs.push(imgData)
                        } catch (chartError) {
                            console.error(`Error processing chart ${i + 1}:`, chartError)
                        }
                    }
                    
                    if (imgs.length === 0) {
                        throw new Error('No charts were successfully processed')
                    }
                    
                    const data = {
                        ApplicationName: this.$t('ApplicationName'),
                        Images: imgs,
                        SurveyName: this.$t('SurveyTitle') + this.surveyName,
                        AverageDifference: this.$t('AvgTimeToFinish') + this.averageDifference,
                        footer: this.$t('CopyRights') + ' ' + new Date().getFullYear()
                    }
                    await SurveyService.exportSurveyToPdf(this.$route.params.id, data)
                } catch (error) {
                    console.error('Error exporting PDF:', error)
                    this.$root.$notification(this.$t('ErrorOccurred') || 'Error occurred while exporting PDF', 'error')
                } finally {
                    this.exportingPdf = false
                }
            },

            async exportToPDFBarAsync() {
                this.exportingPdf = true
                // Use $nextTick to ensure loading indicator is visible
                await this.$nextTick()
                await new Promise(resolve => setTimeout(resolve, 100))
                
                try {
                    const charts = this.$refs.barApex || []
                    const chartsGis = this.$refs.barApexGis || []
                    const allCharts = [...charts, ...chartsGis]
                    const imgs = []
                    
                    for (let i = 0; i < allCharts.length; i++) {
                        try {
                            const imgData = await allCharts[i].capture()
                            if (imgData) imgs.push(imgData)
                        } catch (chartError) {
                            console.error(`Error processing chart ${i + 1}:`, chartError)
                        }
                    }
                    
                    if (imgs.length === 0) {
                        throw new Error('No charts were successfully processed')
                    }

                    const data = {
                        ApplicationName: this.$t('ApplicationName'),
                        Images: imgs,
                        SurveyName: this.$t('SurveyTitle') + this.surveyName,
                        AverageDifference: this.$t('AvgTimeToFinish') + this.averageDifference,
                        footer: this.$t('CopyRights') + ' ' + new Date().getFullYear()
                    }
                    await SurveyService.exportSurveyToPdf(this.$route.params.id, data)
                } catch (error) {
                    console.error('Error exporting PDF:', error)
                    this.$root.$notification(this.$t('ErrorOccurred') || 'Error occurred while exporting PDF', 'error')
                } finally {
                    this.exportingPdf = false
                }
            },

            async exportPdfLineAsync() {
                this.exportingPdf = true
                // Use $nextTick to ensure loading indicator is visible
                await this.$nextTick()
                await new Promise(resolve => setTimeout(resolve, 100))
                
                try {
                    const charts = this.$refs.lineApex || []
                    const chartsGis = this.$refs.lineApexGis || []
                    const allCharts = [...charts, ...chartsGis]
                    const imgs = []
                    
                    for (let i = 0; i < allCharts.length; i++) {
                        try {
                            const imgData = await allCharts[i].capture()
                            if (imgData) imgs.push(imgData)
                        } catch (chartError) {
                            console.error(`Error processing chart ${i + 1}:`, chartError)
                        }
                    }
                    
                    if (imgs.length === 0) {
                        throw new Error('No charts were successfully processed')
                    }

                    const data = {
                        ApplicationName: this.$t('ApplicationName'),
                        Images: imgs,
                        SurveyName: this.$t('SurveyTitle') + this.surveyName,
                        AverageDifference: this.$t('AvgTimeToFinish') + this.averageDifference,
                        footer: this.$t('CopyRights') + ' ' + new Date().getFullYear()
                    }
                    await SurveyService.exportSurveyToPdf(this.$route.params.id, data)
                } catch (error) {
                    console.error('Error exporting PDF:', error)
                    this.$root.$notification(this.$t('ErrorOccurred') || 'Error occurred while exporting PDF', 'error')
                } finally {
                    this.exportingPdf = false
                }
            },

            async exportToPDFTableAsync() {
                this.exportingPdf = true
                // Use $nextTick to ensure loading indicator is visible
                await this.$nextTick()
                await new Promise(resolve => setTimeout(resolve, 100))
                
                try {
                    const tableChart = this.$refs.tableChart
                    const tableChartGis = this.$refs.tableChartGis

                    const svgDataArray = []

                    // Helper function to process a column
                    const processColumn = (col) => {
                        const isArabic = this.isArabicText(col.textContent)

                        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
                        svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
                        svg.setAttribute('width', col.offsetWidth.toString())
                        svg.setAttribute('height', col.offsetHeight.toString())

                        const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject')
                        foreignObject.setAttribute('width', '100%')
                        foreignObject.setAttribute('height', '100%')

                        const divClone = col.cloneNode(true)
                        divClone.querySelectorAll('div').forEach(p => {
                            p.style.textAlign = isArabic ? 'right' : 'left'
                            p.style.direction = isArabic ? 'rtl' : 'ltr'
                        })
                        divClone.querySelectorAll('p').forEach((p, index) => {
                            p.style.textAlign = isArabic ? 'right' : 'left'
                            if (index !== 0) {
                                p.style.opacity = '0.7'
                                p.style.fontWeight = '400'
                            }
                        })

                        foreignObject.appendChild(divClone)
                        svg.appendChild(foreignObject)

                        const svgData = new XMLSerializer().serializeToString(svg)
                        const base64Data = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))

                        return { width: col.offsetWidth.toString(), height: col.offsetHeight.toString(), base: base64Data }
                    }

                    const cols = tableChart ? Array.from(tableChart.querySelectorAll('.col-4')) : []
                    cols.forEach(col => {
                        svgDataArray.push(processColumn(col))
                    })
                    
                    if (tableChartGis) {
                        const colsGis = Array.from(tableChartGis.querySelectorAll('.col-4'))
                        colsGis.forEach(col => {
                            svgDataArray.push(processColumn(col))
                        })
                    }

                    await this.printPdf(svgDataArray)
                } catch (error) {
                    console.error('Error exporting PDF:', error)
                    this.$root.$notification(this.$t('ErrorOccurred') || 'Error occurred while exporting PDF', 'error')
                } finally {
                    this.exportingPdf = false
                }
            },

            async printPdf(svgDataArray) {
                try {
                    // Process all SVG to PNG conversions in parallel
                    const pngPromises = svgDataArray.map(async (svgData) => {
                        const pngData = await this.svgToPngBase64(svgData.base)
                        return 'data:image/png;base64,' + pngData
                    })
                    const imgs = await Promise.all(pngPromises)
                    
                    const data = {
                        ApplicationName: this.$t('ApplicationName'),
                        Images: imgs,
                        SurveyName: this.$t('SurveyTitle') + this.surveyName,
                        AverageDifference: this.$t('AvgTimeToFinish') + this.averageDifference,
                        footer: this.$t('CopyRights') + ' ' + new Date().getFullYear()
                    }
                    await SurveyService.exportSurveyToPdf(this.$route.params.id, data)
                } catch (error) {
                    console.error('Error printing PDF:', error)
                    throw error
                }
            },

            async svgToPngBase64(svgData) {
                try {
                    const img = new Image()
                    img.src = svgData
                    await img.decode()

                    const canvas = document.createElement('canvas')
                    canvas.width = img.width
                    canvas.height = img.height
                    const ctx = canvas.getContext('2d')
                    ctx.drawImage(img, 0, 0)
                    return canvas.toDataURL('image/png').split(',')[1]
                } catch (error) {
                    console.error('Error converting SVG to JPEG:', error)
                    throw error
                }
            },
            isArabicText(text) {
                const arabicRegex = /[\u0600-\u06FF]/
                return arabicRegex.test(text)
            },
            saveAs(blob, filename) {
                if (window.navigator.msSaveOrOpenBlob) {
                    window.navigator.msSaveOrOpenBlob(blob, filename)
                } else {
                    const a = document.createElement('a')
                    document.body.appendChild(a)
                    const url = window.URL.createObjectURL(blob)
                    a.href = url
                    a.download = filename
                    a.click()
                    setTimeout(() => {
                        window.URL.revokeObjectURL(url)
                        document.body.removeChild(a)
                    }, 0)
                }
            }
        },
    }
</script>

<style scoped>
    .odd {
        background: #fff !important
    }

    .custom-table {
        border-collapse: collapse;
        width: 100%;
    }

        .custom-table thead th {
            border: 1px solid #ccc;
            color: #fff !important;
            font-weight: normal !important;
            font-size: 12px !important
        }

    tbody tr:nth-of-type(2n) {
        background-color: #eceded !important;
    }

    .d-flex {
        display: flex;
    }

    .justify-between {
        justify-content: space-between;
    }

    .alternate-custom p {
        padding: 0;
        margin: 0
    }

    * {
        box-shadow: none !important;
    }
</style>